import React from "react";

import type {MaterialSymbol} from "material-symbols";

import {MuiIcon, MuiIconExternalProps} from "./MuiIcon";


/**
 * Wraps a Material Symbols glyph into an `IconProps`-compatible component so it
 * can be passed to `Button`, `Callout`, nav config, etc. — the same way the
 * hand-authored SVG icon components are. Lets us render design-spec glyphs
 * using the existing `MuiIcon` instead of porting bespoke icon markup.
 */
export const createMuiIcon = (code: MaterialSymbol, displayName?: string) => {
    const Icon = React.forwardRef<HTMLElement, MuiIconExternalProps>(
        (props, ref) => <MuiIcon ref={ref} code={code} {...props}/>
    );
    Icon.displayName = displayName ?? `MuiIcon(${code})`;
    return Icon;
};

const iconCache = new Map<string, ReturnType<typeof createMuiIcon>>();

/**
 * Returns a stable (cached) icon component for a glyph name known only at
 * runtime — e.g. when the glyph comes from data. Stable identity avoids
 * remounting the icon on every render.
 */
export const getMuiIcon = (code: string) => {
    let icon = iconCache.get(code);
    if (!icon) {
        icon = createMuiIcon(code as MaterialSymbol);
        iconCache.set(code, icon);
    }
    return icon;
};
